import { useAuthStore } from '../../auth/store';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { Select } from '../../../shared/components/Select';
import { useCharterWaitlist, useUpdateWaitlistEntry } from '../hooks/useCharter';
import { WaitlistStatusBadge } from '../components/CharterStatusBadge';
import {
  CharterHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  SelectMerchantPrompt,
  TableCard,
  TD,
  TD_MUTED,
  TH,
} from '../components/CharterPage';
import { formatWatDate } from '../components/charterFormat';
import { WAITLIST_STATUS_LABELS, WAITLIST_STATUSES, type WaitlistStatus } from '../types';

const STATUS_OPTIONS = WAITLIST_STATUSES.map((s) => ({ value: s, title: WAITLIST_STATUS_LABELS[s] }));

export function CharterWaitlistPage() {
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.CHARTER_MANAGE);

  const { data: entries = [], isLoading, error } = useCharterWaitlist(selectedTenantId);
  const updateEntry = useUpdateWaitlistEntry();

  return (
    <div className="py-6 max-w-5xl mx-auto px-2">
      <CharterHeader
        title="Charter · Waitlist"
        subtitle="Clients who signed up while this provider was at capacity. Work them manually."
      />

      {!selectedTenantId ? (
        <SelectMerchantPrompt what="their Charter waitlist" />
      ) : isLoading ? (
        <LoadingState label="Loading waitlist…" />
      ) : error ? (
        <ErrorState label="Failed to load waitlist." />
      ) : entries.length === 0 ? (
        <EmptyState label="No waitlist signups." />
      ) : (
        <TableCard>
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className={TH}>Customer</th>
                <th className={TH}>Note</th>
                <th className={TH}>Signed up</th>
                <th className={TH}>Status</th>
                {canManage && <th className={TH}>Set status</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {entries.map((e) => (
                <tr key={e.id} className="hover:bg-muted transition-colors">
                  <td className={TD}>
                    <span className="font-medium">{e.customer.name}</span>
                    <span className="block text-xs text-muted-foreground">
                      {e.customer.email}
                      {e.customer.phone ? ` · ${e.customer.phone}` : ''}
                    </span>
                  </td>
                  <td className={`${TD_MUTED} max-w-[240px] truncate`} title={e.note ?? ''}>
                    {e.note ?? '—'}
                  </td>
                  <td className={TD_MUTED}>{formatWatDate(e.createdAt)}</td>
                  <td className="px-6 py-4">
                    <WaitlistStatusBadge status={e.status} />
                  </td>
                  {canManage && (
                    <td className="px-6 py-4 max-w-[180px]">
                      <Select
                        size="small"
                        options={STATUS_OPTIONS}
                        value={e.status}
                        onChange={(v) => updateEntry.mutate({ id: e.id, data: { status: v as WaitlistStatus } })}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </TableCard>
      )}
    </div>
  );
}
